import { AfterViewInit, Directive, ElementRef, OnDestroy, Renderer2 } from "@angular/core";

/**
 * Teleports the host <ui-modal>/<ui-right-panel>'s DOM to document.body, so its
 * `position: fixed` z-index escapes an ancestor stacking context (e.g. Semantic-UI's
 * `.pusher`, or any ancestor with transform/filter/will-change) instead of getting
 * trapped behind page chrome. Angular still owns change detection and bindings normally.
 *
 * Usage: <ui-modal uiModalPortal ...> or <ui-right-panel uiModalPortal ...>. No wiring required.
 */
@Directive({
  standalone: false,
  selector: "ui-modal[uiModalPortal], ui-right-panel[uiModalPortal]"
})
export class UiModalPortalDirective implements AfterViewInit, OnDestroy {
  private originalParent: Node | null = null;
  private originalNextSibling: Node | null = null;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2
  ) {}

  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    this.originalParent = el.parentNode;
    this.originalNextSibling = el.nextSibling;
    this.renderer.appendChild(document.body, el);
  }

  ngOnDestroy(): void {
    const el = this.host.nativeElement;
    // Restore to original position so Angular can dispose cleanly without
    // orphaning DOM nodes that were moved out of the component's view.
    if (this.originalParent) {
      if (this.originalNextSibling && this.originalNextSibling.parentNode === this.originalParent) {
        this.originalParent.insertBefore(el, this.originalNextSibling);
      } else {
        this.originalParent.appendChild(el);
      }
    } else if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  }
}
